import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { Plan, PlanDetail, PlanInfo } from "../../../types/plan";
import config from "../../../utils/config";
import {
  callChatApiV3,
  ChatMessageV3,
  ChatSuccessResponseV3,
  SearchResultV3,
} from "../types/aiChatClientV3";
import { highlightSearchResults } from "../utils/aiChatUtils";

// Determine the API URL based on environment
const API_BASE_URL = config.ServerBackEndUrl;
const CHAT_API_URL = `${API_BASE_URL}/ai/v3/search-spaces`;

export interface SpacesName {
  name: string;
  dbas: string[];
}

export interface AIChatV3 {
  messages: ChatMessageV3[];
  historyMessagesToSend: ChatMessageV3[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  currentSearchResult: SearchResultV3 | null;
  currentHighlight: string | null;
  clearCurrentHighlight: () => void;
  plansInfo: PlanInfo | null;
  distanceData: any | null;

  // Actions
  sendMessage: (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode?: boolean,
    selectedSpaces?: string[]
  ) => Promise<void>;

  clearError: () => void;
  internalCADViewerAICallback: (chatMessage: ChatMessageV3) => void;
  setPlansInfoAndDistanceData: (plansInfo: any, distanceData: any) => void;
  clearHistoryMessagesToSend: () => void;
}

export const useAIChatStoreV3 = create<AIChatV3>((set, get) => ({
  messages: [],
  historyMessagesToSend: [],
  isLoading: false,
  isSearching: false,
  error: null,
  currentSearchResult: null,
  currentHighlight: null,
  plansInfo: null,
  distanceData: null,

  setPlansInfoAndDistanceData: (plansInfo, distanceData) => {
    set({ plansInfo, distanceData });
  },

  clearError: () => {
    set({ error: null, currentSearchResult: null });
  },

  internalCADViewerAICallback: (chatMessage: ChatMessageV3) => {
    const { currentHighlight } = get();

    if (!chatMessage.result) return;

    if (currentHighlight !== chatMessage.uuid) {
      // Convert V3 search results to the format expected by highlightSearchResults
      console.log({ chatMessage });
      const convertedResult = {
        targetPlanInfo: chatMessage.result.reference_space
          ? {
              spaceNumber: chatMessage.result.reference_space.space_number,
              spaceHeader: chatMessage.result.reference_space.space_header,
              summary: { gla: chatMessage.result.reference_space.gla },
              detailTabs: [],
            }
          : undefined,
        searchResults:
          chatMessage.result?.spaces?.map((space) => ({
            spaceID: space.space_number,
            area: parseFloat(space.gla),
            tenant: space.space_header,
            distance: parseFloat(space.distance_m) || 0,
            totalRentPSF: space.rent_per_m2,
            leaseEndDate: space.lease_end_date,
            totalRent: "unknown", // Not available in V3 response
          })) || chatMessage.result.searchResults,
      };

      // Use the utility function for highlighting
      highlightSearchResults(convertedResult as any);
      set({ currentHighlight: chatMessage.uuid ?? null });
    } else {
      // Clear existing highlights
      highlightSearchResults({ targetPlanInfo: undefined, searchResults: [] });
      set({ currentHighlight: null });
    }
  },

  sendMessage: async (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode: boolean = false,
    selectedSpaces?: string[]
  ) => {
    if (!prompt.trim()) return;

    const { plansInfo, historyMessagesToSend, distanceData } = get();

    const metadata = {
      searchMode: useGlobalMode ? ("global" as const) : ("current" as const),
      selectedSpaces: selectedSpaces || [],
      planInfo: !useGlobalMode && selectedPlan && selectedPlanDetail ? {
        propertyName: selectedPlan.propertyName,
        planName: selectedPlanDetail.planName,
      } : undefined,
      timestamp: new Date().toISOString(),
    };

    const newUserMessage: ChatMessageV3 = {
      uuid: uuidv4(),
      role: "user",
      content: prompt,
      metadata,
    };

    // Keep current messages state to pass as history
    const historyToSend = historyMessagesToSend;

    // Optimistically update UI with user message
    set((state) => ({
      messages: [...state.messages, newUserMessage],
      historyMessagesToSend: [...state.historyMessagesToSend, newUserMessage],
      isLoading: true,
      error: null,
      currentSearchResult: null,
    }));

    try {
      console.log(
        "Calling API V3 at:",
        CHAT_API_URL,
        "with prompt:",
        prompt,
        "and history:",
        historyToSend.map((msg) => ({
          role: msg.role,
          content: msg.content,
        }))
      );

      const requestUuid = uuidv4();
      const result: ChatSuccessResponseV3 = await callChatApiV3(
        CHAT_API_URL,
        prompt,
        historyToSend.map((msg) => ({
          uuid: undefined,
          role: msg.role,
          content: msg.content,
        })),
        spacesNames
      );

      console.log("API V3 Response:", result);

      // Handle the search results response
      if (result && result.success) {
        // Convert V3 results to the format expected by the existing display component
        const convertedResult = {
          targetPlanInfo: result.reference_space
            ? {
                spaceNumber: result.reference_space.space_number,
                spaceHeader: result.reference_space.space_header,
                summary: { gla: result.reference_space.gla },
                detailTabs: [],
              }
            : undefined,
          searchResults: result.spaces.map((space) => ({
            spaceID: space.space_number,
            area: parseFloat(space.gla),
            tenant: space.space_header,
            distance: parseFloat(space.distance_m) || 0,
            totalRentPSF: space.rent_per_m2,
            leaseEndDate: space.lease_end_date,
            totalRent: space.total_rent, // Not available in V3 response
          })),
        };

        // Success response - create message with search results
        const assistantMessage: ChatMessageV3 = {
          uuid: requestUuid,
          role: "assistant",
          content: `Found ${
            result.totalCount
          } spaces matching your criteria. Confidence: ${Math.round(
            result.confidence * 100
          )}%`,
          rawContent: JSON.stringify(result, null, 2),
          result: convertedResult as any, // Convert to the format expected by existing components
        };

        set((state) => ({
          messages: [...state.messages, assistantMessage],
          isLoading: false,
          isSearching: false,
          currentSearchResult: result,
          searchResults: [],
        }));

        // Apply highlighting
        setTimeout(() => {
          get().internalCADViewerAICallback(assistantMessage);
        }, 200);

        console.log(
          "Search results added to message:",
          assistantMessage.result
        );
      } else {
        // Error response or no success
        const errorMessage = "Search could not be processed";

        const errorAssistantMessage: ChatMessageV3 = {
          uuid: requestUuid,
          role: "assistant",
          content: errorMessage,
          rawContent: JSON.stringify(result, null, 2),
        };

        set((state) => ({
          messages: [...state.messages, errorAssistantMessage],
          isLoading: false,
          isSearching: false,
        }));
      }
    } catch (err) {
      console.error("Chat API V3 Error in store:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred.";

      // Create an error message to display in chat
      const errorAssistantMessage: ChatMessageV3 = {
        uuid: uuidv4(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${errorMessage}`,
      };

      set((state) => ({
        messages: [...state.messages, errorAssistantMessage],
        isLoading: false,
        isSearching: false,
        error: errorMessage,
      }));
    }
  },

  clearHistoryMessagesToSend: () => {
    set({ historyMessagesToSend: [], messages: [] });
  },

  clearCurrentHighlight: () => {
    set({ currentHighlight: null });
  },
}));
